import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Filter,
  RefreshCw,
  ChevronDown,
  FileText,
  Tag,
  Calendar,
  User,
  Star,
  Eye,
  Edit,
  Trash2,
  X,
  Plus,
  Save,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { server } from "../server";

export default function TopicsList() {
  const [topics, setTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedChapterId, setSelectedChapterId] = useState("");
  const [isPopularFilter, setIsPopularFilter] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [newTopic, setNewTopic] = useState({
    topic: "",
    subjectId: "",
    chapterId: "",
    isPopular: false,
  });

  // Fetch initial data
  useEffect(() => {
    fetchSubjects();
    fetchTopics();
  }, []);

  // Fetch chapters when subject is selected
  useEffect(() => {
    if (selectedSubjectId) {
      fetchChapters(selectedSubjectId);
    } else {
      setChapters([]);
      setSelectedChapterId("");
    }
  }, [selectedSubjectId]);

  const fetchSubjects = async () => {
    try {
      const { data } = await axios.get(`${server}/subject/all`);
      setSubjects(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch subjects");
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchChapters = async (subjectId) => {
    try {
      const { data } = await axios.get(`${server}/chapter/all`, {
        params: { subjectId },
      });
      setChapters(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch chapters");
      console.error("Error fetching chapters:", error);
    }
  };

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/topics/all`);
      setTopics(data.data || []);
    } catch (error) {
      toast.error("Failed to fetch topics");
      console.error("Error fetching topics:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTopic = async (e) => {
    e.preventDefault();
    if (!newTopic.topic.trim()) {
      toast.error("Please enter a topic name");
      return;
    }
    if (!newTopic.subjectId) {
      toast.error("Please select a subject");
      return;
    }
    if (!newTopic.chapterId) {
      toast.error("Please select a chapter");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.post(`${server}/topics/new`, null, {
        params: {
          topic: newTopic.topic.trim(),
          subjectId: parseInt(newTopic.subjectId),
          chapterId: parseInt(newTopic.chapterId),
          isPopular: newTopic.isPopular,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Topic added successfully!");
        resetNewTopic();
        setShowAddModal(false);
        fetchTopics();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add topic");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditTopic = async (topic) => {
    if (!newTopic.topic.trim()) {
      toast.error("Please enter a topic name");
      return;
    }
    if (!newTopic.subjectId) {
      toast.error("Please select a subject");
      return;
    }
    if (!newTopic.chapterId) {
      toast.error("Please select a chapter");
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.put(`${server}/topics/updateById`, null, {
        params: {
          topicId: topic.id,
          topic: newTopic.topic.trim(),
          subjectId: parseInt(newTopic.subjectId),
          chapterId: parseInt(newTopic.chapterId),
          isPopular: newTopic.isPopular,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Topic updated successfully!");
        setEditingTopic(null);
        resetNewTopic();
        fetchTopics();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update topic");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    if (!window.confirm("Are you sure you want to delete this topic?")) {
      return;
    }

    try {
      setSubmitting(true);
      const { data } = await axios.delete(`${server}/topics/deleteById`, {
        params: {
          topicId: topicId,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (data.success) {
        toast.success("Topic deleted successfully!");
        fetchTopics();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete topic");
    } finally {
      setSubmitting(false);
    }
  };

  const resetNewTopic = () => {
    setNewTopic({
      topic: "",
      subjectId: "",
      chapterId: "",
      isPopular: false,
    });
  };

  const startEdit = (topic) => {
    setEditingTopic(topic);
    setNewTopic({
      topic: topic.topic,
      subjectId: topic.subjectId?.toString(),
      chapterId: topic.chapterId?.toString(),
      isPopular: topic.isPopular || false,
    });
  };

  const cancelEdit = () => {
    setEditingTopic(null);
    resetNewTopic();
  };

  // Filter topics based on all criteria
  const filteredTopics = topics.filter((topic) => {
    const matchesSearch = topic.topic
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesSubject = selectedSubjectId
      ? topic.subjectId === parseInt(selectedSubjectId)
      : true;

    const matchesChapter = selectedChapterId
      ? topic.chapterId === parseInt(selectedChapterId)
      : true;

    const matchesPopular =
      isPopularFilter !== ""
        ? topic.isPopular === (isPopularFilter === "true")
        : true;

    const matchesDeleted = showDeleted ? topic.deleted : !topic.deleted;

    return (
      matchesSearch &&
      matchesSubject &&
      matchesChapter &&
      matchesPopular &&
      matchesDeleted
    );
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedSubjectId("");
    setSelectedChapterId("");
    setIsPopularFilter("");
    setShowDeleted(false);
  };

  // Check if any filter is active
  const hasActiveFilters =
    searchTerm ||
    selectedSubjectId ||
    selectedChapterId ||
    isPopularFilter !== "" ||
    showDeleted;

  const getSubjectName = (subjectId) => {
    const subject = subjects.find((s) => s.id === subjectId);
    return subject ? subject.subject : "Unknown Subject";
  };

  const getChapterName = (chapterId) => {
    const chapter = chapters.find((c) => c.id === chapterId);
    return chapter ? chapter.chapter : "Unknown Chapter";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">All Topics</h1>
          </div>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Browse and manage all topics across subjects and chapters. Use
            filters to find specific topics.
          </p>
        </motion.div>

        {/* Filters Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gray-800 rounded-2xl p-6 mb-6 shadow-lg border border-gray-700"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between mb-4">
            {/* Search */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-600 text-gray-300 border border-gray-500 rounded-xl hover:bg-gray-500 transition-all duration-200"
                >
                  <X className="w-4 h-4" />
                  Clear Filters
                </button>
              )}

              {/* Show Deleted Toggle */}
              <button
                onClick={() => setShowDeleted(!showDeleted)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-200 ${
                  showDeleted
                    ? "bg-red-500/20 text-red-400 border border-red-500/30"
                    : "bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600"
                }`}
              >
                <Eye className="w-4 h-4" />
                {showDeleted ? "Hide Deleted" : "Show Deleted"}
              </button>

              {/* Refresh */}
              <button
                onClick={fetchTopics}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-3 bg-gray-700 text-gray-300 border border-gray-600 rounded-xl hover:bg-gray-600 transition-all duration-200 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                />
                Refresh
              </button>

              {/* Add Topic */}
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200"
              >
                <Plus className="w-4 h-4" />
                Add Topic
              </button>
            </div>
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Subject Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subject
              </label>
              <div className="relative">
                <select
                  value={selectedSubjectId}
                  onChange={(e) => setSelectedSubjectId(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="">All Subjects</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Chapter Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Chapter
              </label>
              <div className="relative">
                <select
                  value={selectedChapterId}
                  onChange={(e) => setSelectedChapterId(e.target.value)}
                  disabled={!selectedSubjectId}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">All Chapters</option>
                  {chapters.map((chapter) => (
                    <option key={chapter.id} value={chapter.id}>
                      {chapter.chapter}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Popular Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Popular
              </label>
              <div className="relative">
                <select
                  value={isPopularFilter}
                  onChange={(e) => setIsPopularFilter(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="">All Topics</option>
                  <option value="true">Popular Only</option>
                  <option value="false">Not Popular</option>
                </select>
                <Star className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Status
              </label>
              <div className="relative">
                <select
                  value={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.value === "true")}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                >
                  <option value="false">Active</option>
                  <option value="true">Deleted</option>
                </select>
                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Active Filters Indicator */}
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap gap-2">
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-xs">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm("")}
                    className="hover:text-blue-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedSubjectId && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-lg text-xs">
                  Subject: {getSubjectName(parseInt(selectedSubjectId))}
                  <button
                    onClick={() => setSelectedSubjectId("")}
                    className="hover:text-green-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedChapterId && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/20 text-purple-400 rounded-lg text-xs">
                  Chapter: {getChapterName(parseInt(selectedChapterId))}
                  <button
                    onClick={() => setSelectedChapterId("")}
                    className="hover:text-purple-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {isPopularFilter !== "" && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-lg text-xs">
                  Popular: {isPopularFilter === "true" ? "Yes" : "No"}
                  <button
                    onClick={() => setIsPopularFilter("")}
                    className="hover:text-yellow-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {showDeleted && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 rounded-lg text-xs">
                  Status: Deleted
                  <button
                    onClick={() => setShowDeleted(false)}
                    className="hover:text-red-300"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </motion.div>

        {/* Topics List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
          ) : filteredTopics.length === 0 ? (
            <div className="text-center py-12 bg-gray-800 rounded-2xl border border-gray-700">
              <FileText className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-400 mb-2">
                {hasActiveFilters
                  ? "No topics match your filters"
                  : showDeleted
                  ? "No deleted topics found"
                  : "No topics found"}
              </h3>
              <p className="text-gray-500">
                {hasActiveFilters
                  ? "Try adjusting your filters or search terms"
                  : showDeleted
                  ? "Deleted topics will appear here"
                  : "Get started by creating your first topic"}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredTopics.map((topic) => (
                <motion.div
                  key={topic.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`bg-gray-800 rounded-2xl p-6 border transition-all duration-200 ${
                    topic.deleted
                      ? "border-red-500/30 bg-red-500/5"
                      : "border-gray-700 hover:border-gray-600"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Topic Info */}
                    <div className="flex-1">
                      {editingTopic?.id === topic.id ? (
                        <div className="space-y-4">
                          <input
                            type="text"
                            value={newTopic.topic}
                            onChange={(e) =>
                              setNewTopic({
                                ...newTopic,
                                topic: e.target.value,
                              })
                            }
                            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Topic name..."
                            autoFocus
                          />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="relative">
                              <select
                                value={newTopic.subjectId}
                                onChange={(e) =>
                                  setNewTopic({
                                    ...newTopic,
                                    subjectId: e.target.value,
                                  })
                                }
                                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                              >
                                <option value="">Select Subject</option>
                                {subjects.map((subject) => (
                                  <option key={subject.id} value={subject.id}>
                                    {subject.subject}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>
                            <div className="relative">
                              <select
                                value={newTopic.chapterId}
                                onChange={(e) =>
                                  setNewTopic({
                                    ...newTopic,
                                    chapterId: e.target.value,
                                  })
                                }
                                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                              >
                                <option value="">Select Chapter</option>
                                {chapters.map((chapter) => (
                                  <option key={chapter.id} value={chapter.id}>
                                    {chapter.chapter}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="editIsPopular"
                              checked={newTopic.isPopular}
                              onChange={(e) =>
                                setNewTopic({
                                  ...newTopic,
                                  isPopular: e.target.checked,
                                })
                              }
                              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <label
                              htmlFor="editIsPopular"
                              className="text-sm text-gray-300 flex items-center gap-1"
                            >
                              <Star className="w-4 h-4 text-yellow-400" />
                              Popular Topic
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <h3 className="text-xl font-semibold text-white">
                              {topic.topic}
                            </h3>
                            {topic.isPopular && (
                              <span className="flex items-center gap-1 px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium">
                                <Star className="w-3 h-3" />
                                Popular
                              </span>
                            )}
                            {topic.deleted && (
                              <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                                Deleted
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                            <div className="flex items-center gap-1">
                              <Tag className="w-4 h-4" />
                              <span>
                                Subject: {getSubjectName(topic.subjectId)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FileText className="w-4 h-4" />
                              <span>
                                Chapter: {getChapterName(topic.chapterId)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(topic.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>By User {topic.createdBy}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {editingTopic?.id === topic.id ? (
                        <>
                          <button
                            onClick={() => handleEditTopic(topic)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                          >
                            <Save className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-gray-300 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </>
                      ) : topic.deleted ? (
                        <span className="text-red-400 text-sm">Deleted</span>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(topic)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteTopic(topic.id)}
                            disabled={submitting}
                            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Stats */}
          {!loading && topics.length > 0 && (
            <motion.div
              className="mt-6 text-center text-gray-400 text-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Showing {filteredTopics.length} of {topics.length} topics
              {hasActiveFilters && " (filtered)"}
              {showDeleted && " (including deleted)"}
            </motion.div>
          )}
        </motion.div>
      </div>

      {/* Add Topic Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="bg-gray-800 rounded-2xl p-6 max-w-md w-full border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">
                  Add New Topic
                </h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleAddTopic} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Topic Name *
                  </label>
                  <input
                    type="text"
                    value={newTopic.topic}
                    onChange={(e) =>
                      setNewTopic({ ...newTopic, topic: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter topic name..."
                    required
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Subject *
                  </label>
                  <div className="relative">
                    <select
                      value={newTopic.subjectId}
                      onChange={(e) =>
                        setNewTopic({ ...newTopic, subjectId: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                      required
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((subject) => (
                        <option key={subject.id} value={subject.id}>
                          {subject.subject}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Chapter *
                  </label>
                  <div className="relative">
                    <select
                      value={newTopic.chapterId}
                      onChange={(e) =>
                        setNewTopic({ ...newTopic, chapterId: e.target.value })
                      }
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                      required
                    >
                      <option value="">Select Chapter</option>
                      {chapters.map((chapter) => (
                        <option key={chapter.id} value={chapter.id}>
                          {chapter.chapter}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isPopular"
                    checked={newTopic.isPopular}
                    onChange={(e) =>
                      setNewTopic({ ...newTopic, isPopular: e.target.checked })
                    }
                    className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label
                    htmlFor="isPopular"
                    className="text-sm text-gray-300 flex items-center gap-1"
                  >
                    <Star className="w-4 h-4 text-yellow-400" />
                    Mark as Popular Topic
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-3 bg-gray-700 text-gray-300 rounded-xl hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-br from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-200 disabled:opacity-50"
                  >
                    {submitting ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    {submitting ? "Adding..." : "Add Topic"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
